import * as React from 'react'
import './styles/index.css'

import type { Content, Editor } from '@tiptap/react'
import type { UseMinimalTiptapEditorProps } from './hooks/use-minimal-tiptap'
import { EditorContent } from '@tiptap/react'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { SectionOne } from './components/section/one'
import { SectionTwo } from './components/section/two'
import { SectionThree } from './components/section/three'
import { SectionFour } from './components/section/four'
import { SectionFive } from './components/section/five'
import { SectionSix } from './components/section/six'
import { LinkBubbleMenu } from './components/bubble-menu/link-bubble-menu'
import { useMinimalTiptapEditor } from './hooks/use-minimal-tiptap'
import { MeasuredContainer } from './components/measured-container'

export interface MinimalTiptapProps
  extends Omit<UseMinimalTiptapEditorProps, 'onUpdate'> {
  value?: Content
  onChange?: (value: Content) => void
  className?: string
  editorContentClassName?: string
  modal?: boolean
}

const Toolbar = ({ editor, modal }: { editor: Editor; modal?: boolean }) => (
  <div className="shrink-0 border-b border-[rgba(255,255,255,0.06)] focus-within:border-[rgba(255,255,255,0.2)] p-2">
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-px">
        <SectionOne editor={editor} activeLevels={[1, 2, 3, 4, 5, 6]} />
      </div>
      <Separator orientation="vertical" className="h-7 hidden sm:block" />
      <div className="flex items-center gap-px">
        <SectionTwo
          editor={editor}
          activeActions={[
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'code',
            'clearFormatting',
          ]}
          mainActionCount={3}
        />
      </div>
      <Separator orientation="vertical" className="h-7 hidden sm:block" />
      <div className="flex items-center gap-px">
        <SectionThree editor={editor} modal={modal} />
      </div>
      <Separator orientation="vertical" className="h-7 hidden sm:block" />
      <div className="flex items-center gap-px">
        <SectionFour
          editor={editor}
          activeActions={['orderedList', 'bulletList']}
          mainActionCount={0}
          modal={modal}
        />
        <SectionFive editor={editor} mainActionCount={0} modal={modal} />
      </div>
      <Separator orientation="vertical" className="h-7 hidden sm:block" />
      <div className="flex items-center gap-px">
        <SectionSix
          editor={editor}
          activeActions={['codeBlock', 'blockquote', 'horizontalRule']}
          mainActionCount={0}
          modal={modal}
        />
      </div>
    </div>
  </div>
)

export const MinimalTiptapEditor = React.forwardRef<
  HTMLDivElement,
  MinimalTiptapProps
>(
  (
    { value, onChange, className, editorContentClassName, modal, ...props },
    ref,
  ) => {
    const editor = useMinimalTiptapEditor({
      value,
      onUpdate: onChange,
      ...props,
    })

    if (!editor) {
      return null
    }

    return (
      <MeasuredContainer
        as="div"
        name="editor"
        ref={ref}
        className={cn(
          'flex h-auto min-h-48 sm:min-h-72 w-full max-w-full flex-col rounded-md border border-[rgba(255,255,255,0.06)] shadow-sm focus-within:border-[rgba(255,255,255,0.2)]',
          className,
        )}
      >
        <Toolbar editor={editor} modal={modal} />
        <EditorContent
          editor={editor}
          className={cn('minimal-tiptap-editor', editorContentClassName)}
        />
        <LinkBubbleMenu editor={editor} />
      </MeasuredContainer>
    )
  },
)

MinimalTiptapEditor.displayName = 'MinimalTiptapEditor'

export default MinimalTiptapEditor
